import React from 'react';
import { Badge, Table } from 'reactstrap';
import {
  parseOrderLineItemMeta,
  metaArrayToRecord,
  formatOrderTotal,
  getOrderLineItemDisplayName,
  type LineItemOrderType,
} from '../../utils/orderLineItemMeta';

const TYPE_BADGE: Record<LineItemOrderType, { color: string; label: string }> = {
  hotel: { color: 'info', label: 'Hotel booking' },
  transfer: { color: 'primary', label: 'Transfer' },
  tour: { color: 'success', label: 'Tour' },
  package: { color: 'secondary', label: 'Package' },
  generic: { color: 'secondary', label: 'Package' },
};

interface OrderLineItemDetailsProps {
  productName: string;
  meta?: Array<{ key: string; value: string }>;
  quantity?: number | null;
  total?: string | null;
  currency?: string;
}

const OrderLineItemDetails: React.FC<OrderLineItemDetailsProps> = ({
  productName,
  meta,
  quantity,
  total,
  currency,
}) => {
  const record = metaArrayToRecord(meta);
  const parsed = parseOrderLineItemMeta(record, productName);
  const badge = TYPE_BADGE[parsed.type];
  const displayName = getOrderLineItemDisplayName(productName, meta);

  return (
    <div className="order-line-item-details">
      <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
        <strong className="me-1">{displayName}</strong>
        <Badge color={badge.color} pill className="font-size-11">
          {badge.label}
        </Badge>
      </div>

      {parsed.thumbnailUrl && (
        <img
          src={parsed.thumbnailUrl}
          alt=""
          className="rounded mb-2"
          style={{ maxWidth: 120, maxHeight: 80, objectFit: 'cover' }}
        />
      )}

      {parsed.sections.map((sec) => (
        <div key={sec.title} className="mb-2">
          <div className="text-muted text-uppercase font-size-11 fw-semibold mb-1">
            {sec.title}
          </div>
          <Table size="sm" borderless className="mb-0 order-meta-table">
            <tbody>
              {sec.fields.map((f) => (
                <tr key={`${sec.title}-${f.label}`}>
                  <td className="text-muted pe-2 align-top" style={{ width: '38%' }}>
                    {f.label}
                  </td>
                  <td className="align-top">
                    {f.isUrl ? (
                      <a href={f.value} target="_blank" rel="noopener noreferrer" className="small">
                        View link
                      </a>
                    ) : (
                      f.value
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ))}

      {parsed.travelers.length > 0 && (
        <div className="mb-2">
          <div className="text-muted text-uppercase font-size-11 fw-semibold mb-1">
            Travelers
          </div>
          <Table size="sm" bordered responsive className="mb-0 font-size-12">
            <thead className="table-light">
              <tr>
                <th>Room</th>
                <th>Type</th>
                <th>Name</th>
                <th>DOB</th>
                <th>Nationality</th>
              </tr>
            </thead>
            <tbody>
              {parsed.travelers.map((t, i) => (
                <tr key={`${t.name}-${i}`}>
                  <td>{t.room || '—'}</td>
                  <td>{t.type}</td>
                  <td>{t.name}</td>
                  <td>{t.dob || '—'}</td>
                  <td>{t.nationality || '—'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <div className="d-flex justify-content-end gap-3 text-muted small mt-1">
        <span>
          Qty: <span className="text-dark">{quantity ?? '—'}</span>
        </span>
        {total != null && total !== '' && (
          <span>
            Line total:{' '}
            <span className="text-dark fw-medium">
              {formatOrderTotal(total, currency)}
            </span>
          </span>
        )}
      </div>
    </div>
  );
};

export default OrderLineItemDetails;
